import React, { useState } from "react";
import { Link } from "@/components/Link";
import type { Store } from "@/api/storeService";
import { branchCountLabel, formatRating, toArabicNumerals } from "@/lib/arabicNumerals";
import StoreSocialLinks from "@/components/StoreSocialLinks";
import StoreVerifiedBadge from "@/components/StoreVerifiedBadge";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { storeEventProps } from "@/lib/posthog";
import { aggregateBranchGoogleRating } from "@/lib/branchGoogleRating";

// ─── Rating sources ───────────────────────────────────────────────────────────

type RatingSource = {
  score: number;
  count: number;
  // Set ONLY when `score` is a review-count-weighted mean across MULTIPLE Google places (one per
  // branch) rather than one place's own rating. The presence of this field is what switches the
  // badge and subline into branch-aware copy and drops the single-placeId link.
  branches?: { total: number; rated: number };
};

// We deliberately do NOT blend community + Google into one number: they measure
// different things (online-buying experience vs the physical shop/brand) and have
// wildly different sample sizes, so a count-weighted average would erase the
// community signal. Each source is shown separately and attributed instead.
function getRatings(store: Store): {
  community: RatingSource | null;
  google: RatingSource | null;
} {
  const community =
    store.totalReviews > 0
      ? { score: store.rating, count: store.totalReviews }
      : null;

  // For a multi-branch store `store.googlePlaces` is a mirror of the PRIMARY branch alone (see
  // syncPrimaryBranchToGooglePlaces), so it structurally cannot represent the chain — it was
  // showing one clinic's 4.5·53 for an 8-branch business. Aggregate the branches instead.
  // Single-branch and branch-less stores take the identical expression they always did.
  const branchAggregate = aggregateBranchGoogleRating(store.branches);
  const google =
    store.branches && store.branches.length >= 2 && branchAggregate
      ? {
          score: branchAggregate.score,
          count: branchAggregate.count,
          branches: {
            total: branchAggregate.totalBranches,
            rated: branchAggregate.ratedBranches,
          },
        }
      : store.googlePlaces?.reviewCount && store.googlePlaces.reviewCount > 0
        ? {
            score: store.googlePlaces.rating,
            count: store.googlePlaces.reviewCount,
          }
        : null;
  return { community, google };
}

// «٦ من ٨ فروع» when only some branches carry a Google rating, «٨ فروع» when they all do.
// Stating coverage matters: a bare «٨ فروع» beside the score would imply the average covers all
// eight when it may cover one. Kept as a standalone segment, never governed by a preposition —
// branchCountLabel(2) returns the nominative dual «فرعان», which would have to be the genitive
// «فرعين» after «من», and the 2-branch store is one of only two live cases.
function branchCoverageLabel(branches: { total: number; rated: number }): string {
  const total = branchCountLabel(branches.total, toArabicNumerals);
  return branches.rated === branches.total
    ? total
    : `${toArabicNumerals(branches.rated)} من ${total}`;
}

// The Google subline under the headline number, used by BOTH the desktop and mobile layouts so
// the two duplicated blocks cannot drift apart.
function googleSublineText(source: RatingSource): string {
  return source.branches
    ? `متوسط خرائط جوجل · ${toArabicNumerals(source.count)} تقييم · ${branchCoverageLabel(source.branches)}`
    : `تقييم خرائط جوجل · ${toArabicNumerals(source.count)}`;
}

function googleMapsUrl(store: Store): string | undefined {
  const placeId = store.googlePlaces?.placeId;
  return placeId
    ? `https://www.google.com/maps/place/?q=place_id:${placeId}`
    : undefined;
}

// ─── Logo box ────────────────────────────────────────────────────────────────

function LogoBox({
  store,
  size,
  isEditMode,
  logoRef,
  onLogoUpload,
  isUploadingLogo,
}: {
  store: Store;
  size: number;
  isEditMode?: boolean;
  logoRef?: React.RefObject<HTMLInputElement>;
  onLogoUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingLogo?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        minWidth: size,
        background: "var(--ys-paper)",
        border: "1px solid var(--ys-rule)",
        borderRadius: "var(--ys-r-md)",
        overflow: "hidden",
        boxShadow: "var(--ys-shadow-1)",
        flexShrink: 0,
      }}
    >
      <img
        src={store.logoUrl || "/placeholder.svg"}
        alt={store.name}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
      {isEditMode && logoRef && onLogoUpload && (
        <>
          <input
            ref={logoRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onLogoUpload}
          />
          <button
            type="button"
            onClick={() => logoRef.current?.click()}
            disabled={isUploadingLogo}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,.5)",
              borderRadius: "var(--ys-r-md)",
              opacity: hovered ? 1 : 0,
              transition: "opacity .2s",
              cursor: "pointer",
              border: "none",
            }}
          >
            {isUploadingLogo ? (
              <div
                style={{
                  width: 20,
                  height: 20,
                  border: "2px solid #fff",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            ) : (
              <svg
                style={{ width: 22, height: 22, color: "#fff" }}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            )}
          </button>
        </>
      )}
    </div>
  );
}

// ─── Google rating badge (attributed secondary) ───────────────────────────────

function GoogleBadge({
  source,
  href,
  onClick,
}: {
  source: RatingSource;
  href?: string;
  onClick?: () => void;
}) {
  // The pill is compressed; spell out what the number means for anyone who hovers or uses a
  // screen reader, since "متوسط" alone does not say what it is weighted by.
  const aggregateHint = source.branches
    ? "متوسط تقييمات خرائط جوجل لكل الفروع، مرجّح بعدد المراجعات"
    : undefined;

  const inner = (
    <span
      title={aggregateHint}
      aria-label={aggregateHint}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "var(--ys-font)",
        fontSize: 12,
        color: "var(--ys-ink-2)",
        border: "1px solid var(--ys-rule)",
        borderRadius: "var(--ys-r-pill)",
        padding: "3px 10px",
        background: "var(--ys-paper)",
        whiteSpace: "nowrap",
        direction: "rtl",
      }}
    >
      <span>{source.branches ? "متوسط خرائط جوجل" : "خرائط جوجل"}</span>
      <span style={{ direction: "ltr", color: "var(--ys-star)" }}>★</span>
      <span
        style={{ direction: "ltr", fontWeight: 600, color: "var(--ys-ink)" }}
      >
        {formatRating(source.score)}
      </span>
      <span style={{ color: "var(--ys-ink-3)" }}>
        · {toArabicNumerals(source.count)}
        {source.branches ? " تقييم" : ""}
      </span>
      {source.branches && (
        <span style={{ color: "var(--ys-ink-3)" }}>
          · {branchCoverageLabel(source.branches)}
        </span>
      )}
    </span>
  );

  if (!href) return inner;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      style={{ textDecoration: "none" }}
    >
      {inner}
    </a>
  );
}

// ─── Rating block (desktop column) ─────────────────────────────────────────────

function RatingBlock({
  community,
  google,
  googleHref,
  onGoogleClick,
}: {
  community: RatingSource | null;
  google: RatingSource | null;
  googleHref?: string;
  onGoogleClick?: () => void;
}) {
  const frame = (children: React.ReactNode) => (
    <div
      style={{
        borderRight: "1px solid var(--ys-rule)",
        borderLeft: "1px solid var(--ys-rule)",
        padding: "0 24px",
        textAlign: "center",
        minWidth: 150,
      }}
    >
      {children}
    </div>
  );

  if (!community && !google) {
    return frame(
      <div
        style={{
          fontFamily: "var(--ys-font)",
          fontSize: 13,
          color: "var(--ys-ink-3)",
          whiteSpace: "nowrap",
        }}
      >
        لم يقيّم بعد
      </div>
    );
  }

  // Community is the primary signal; fall back to Google only when no community
  // reviews exist yet.
  const primary = community ?? google!;
  const primaryIsCommunity = !!community;

  return frame(
    <div
      style={{
        direction: "ltr",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: "var(--ys-font)",
            fontWeight: 700,
            fontSize: 48,
            lineHeight: 1,
            color: "var(--ys-ink)",
          }}
        >
          {formatRating(primary.score)}
          <sub
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 11,
              color: "var(--ys-ink-3)",
              fontWeight: 400,
              verticalAlign: "baseline",
              marginRight: 2,
            }}
          >
            {" / ٥"}
          </sub>
        </div>
        <div
          style={{
            fontFamily: "var(--ys-font)",
            fontSize: 13,
            color: "var(--ys-ink-3)",
            marginTop: 6,
            direction: "rtl",
            whiteSpace: "nowrap",
          }}
        >
          {primaryIsCommunity
            ? `مبني على ${toArabicNumerals(primary.count)} تقييم`
            : googleSublineText(primary)}
        </div>
      </div>

      {primaryIsCommunity && google && (
        <GoogleBadge source={google} href={googleHref} onClick={onGoogleClick} />
      )}

      {!primaryIsCommunity && (
        <div
          style={{
            fontFamily: "var(--ys-font)",
            fontSize: 12,
            color: "var(--ys-ink-3)",
            direction: "rtl",
            whiteSpace: "nowrap",
          }}
        >
          كن أول من يقيّم على يتفرّج
        </div>
      )}
    </div>
  );
}

// ─── CTA column ──────────────────────────────────────────────────────────────

function CTAColumn({
  onScrollToReviews,
}: {
  onScrollToReviews?: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        minWidth: 160,
      }}
    >
      <button
        onClick={onScrollToReviews}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          fontFamily: "var(--ys-font)",
          fontWeight: 600,
          fontSize: 14,
          background: hovered
            ? "var(--ys-primary-hover)"
            : "var(--ys-primary)",
          color: "#fff",
          border: "none",
          borderRadius: "var(--ys-r)",
          padding: "10px 20px",
          cursor: "pointer",
          transition: "background 180ms var(--ys-ease)",
          whiteSpace: "nowrap",
        }}
      >
        ✍ اكتب تقييمك
      </button>
      <span
        style={{
          fontFamily: "var(--ys-font)",
          fontSize: 12,
          color: "var(--ys-ink-3)",
        }}
      >
        شاركنا تجربتك مع المتجر
      </span>
    </div>
  );
}

// ─── Mobile write-review button (quick-260815-mkf) ──────────────────────────
// Compact CTA that lives inside the mobile rating row (both the rated and
// no-rating branches). Mobile tree only — CTAColumn stays on desktop.

function MobileReviewButton({
  onScrollToReviews,
}: {
  onScrollToReviews?: () => void;
}) {
  return (
    <button
      onClick={onScrollToReviews}
      style={{
        fontFamily: "var(--ys-font)",
        fontWeight: 600,
        fontSize: 13,
        background: "var(--ys-primary)",
        color: "#fff",
        border: "none",
        borderRadius: "var(--ys-r)",
        padding: "8px 16px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      ✍ اكتب تقييمك
    </button>
  );
}

// ─── Verified pill ───────────────────────────────────────────────────────────

function VerifiedPill({ verified }: { verified: boolean }) {
  const color = verified ? "var(--ys-success)" : "var(--ys-warning)";
  const label = verified ? "موثّق" : "غير موثق";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontFamily: "var(--ys-font)",
        fontSize: 12,
        fontWeight: 500,
        color,
        border: `1px solid ${color}`,
        borderRadius: "var(--ys-r-pill)",
        padding: "3px 10px",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {label}
    </span>
  );
}

// ─── Campaign badge ──────────────────────────────────────────────────────────

// `metadata` is Record<string, unknown>, so the audit verdict is narrowed here rather than
// indexed untyped at the call site.
type ReviewAuditMeta = { reviewAudit?: { status?: string; flaggedCount?: number } };

const readAuditStatus = (metadata: Record<string, unknown> | undefined) =>
  (metadata as ReviewAuditMeta | undefined)?.reviewAudit?.status;

// Shown for campaign_detected AND suspected_fraud with IDENTICAL wording: the badge states a
// fact about the reviews (we found an inflated campaign and excluded it), it does not accuse the
// merchant. Fraud keeps its separate treatment through the existing flaggedFraud path.
function CampaignBadge({ status }: { status?: string }) {
  if (status !== "campaign_detected" && status !== "suspected_fraud") return null;

  return (
    <span
      title="رصدنا تقييمات مضخّمة على صفحة هذا المتجر على فيسبوك — استبعدناها من التقييم."
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontFamily: "var(--ys-font)",
        fontSize: 12,
        fontWeight: 500,
        color: "#92400e",
        background: "#fef3c7",
        border: "1px solid #f59e0b",
        borderRadius: "var(--ys-r-pill)",
        padding: "3px 10px",
      }}
    >
      ⚠️ حملة تقييمات مرصودة
    </span>
  );
}

// ─── Chip (category / location) ───────────────────────────────────────────────

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "var(--ys-font)",
        fontWeight: 500,
        fontSize: 12,
        color: "var(--ys-ink-2)",
        border: "1px solid var(--ys-rule)",
        borderRadius: "var(--ys-r-pill)",
        padding: "3px 10px",
        background: "var(--ys-paper)",
      }}
    >
      {children}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export type StorePageHeaderProps = {
  store: Store;
  isEditMode?: boolean;
  logoRef?: React.RefObject<HTMLInputElement>;
  onLogoUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingLogo?: boolean;
  onScrollToReviews?: () => void;
};

export default function StorePageHeader({
  store,
  isEditMode,
  logoRef,
  onLogoUpload,
  isUploadingLogo,
  onScrollToReviews,
}: StorePageHeaderProps) {
  const { community, google } = getRatings(store);
  const { trackStore } = usePostHogTracking();
  // No single URL represents an average of N places, so the aggregate badge is unlinked.
  // Per-branch Google links already exist in the branches sheet.
  const googleHref = google && !google.branches ? googleMapsUrl(store) : undefined;
  const handleGoogleBadgeClick = () =>
    trackStore.contactClicked({
      platform: "google_maps",
      surface: "store_header",
      ...storeEventProps(store),
    });

  // The location chip is a single string and reads as one address; for a chain this is the
  // cheapest "there is more than one of these" signal. Total branches, not rated ones — the chip
  // is about locations, not about Google coverage. Non-interactive by design.
  const branchCount = Array.isArray(store.branches) ? store.branches.length : 0;
  const primary = community ?? google;
  const primaryIsCommunity = !!community;
  // Fake-review-campaign verdict, written by reviewAuthenticityService onto the store document.
  const auditStatus = readAuditStatus(store.metadata);
  const categoryAr =
    store.prominentCategory?.category?.title?.ar ??
    store.category?.title?.ar;
  const categorySlug =
    store.prominentCategory?.category?.slug ?? store.category?.slug;

  const SEP = (
    <span style={{ color: "var(--ys-rule-2)", margin: "0 2px" }}>·</span>
  );

  const breadcrumb = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <Link to="/stores" style={{ color: "inherit", textDecoration: "none" }}>
        المتاجر
      </Link>
      {categoryAr && (
        <>
          {SEP}
          <Link
            to={`/category/${categorySlug}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            {categoryAr}
          </Link>
        </>
      )}
      {SEP}
      <strong style={{ color: "var(--ys-ink)", fontWeight: 600 }}>{store.name}</strong>
    </span>
  );

  return (
    <>
      {/* ── Desktop (≥ lg / 1024px) ── */}
      <div
        className="hidden lg:block"
        style={{
          background: "var(--ys-paper)",
          borderBottom: "1px solid var(--ys-rule)",
          direction: "rtl",
        }}
      >
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            padding: "18px 40px",
          }}
        >
          {/* Breadcrumb */}
          <div
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 12,
              color: "var(--ys-ink-3)",
              marginBottom: 14,
            }}
          >
            {breadcrumb}
          </div>

          {/* 4-column row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "104px 1fr auto auto",
              gap: 26,
              alignItems: "center",
            }}
          >
            {/* Logo */}
            <LogoBox
              store={store}
              size={104}
              isEditMode={isEditMode}
              logoRef={logoRef}
              onLogoUpload={onLogoUpload}
              isUploadingLogo={isUploadingLogo}
            />

            {/* Identity */}
            <div>
              <h1
                style={{
                  fontFamily: "var(--ys-font)",
                  fontWeight: 600,
                  fontSize: 30,
                  margin: 0,
                  lineHeight: 1.05,
                  color: "var(--ys-ink)",
                  display: "flex",
                  alignItems: "baseline",
                  flexWrap: "wrap",
                  gap: "0 8px",
                }}
              >
                {store.name}
                {store.nameEn && (
                  <span
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontWeight: 600,
                      fontSize: 20,
                      color: "var(--ys-primary)",
                    }}
                  >
                    {store.nameEn}
                  </span>
                )}
              </h1>

              {/* Meta pills */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 10,
                  flexWrap: "wrap",
                }}
              >
                <VerifiedPill verified={Boolean(store.verified || store.hasVerifiedOwner)} />
                <StoreVerifiedBadge show={store.hasVerifiedOwner} />
                <CampaignBadge status={auditStatus} />
                {categoryAr && (
                  <Link
                    to={`/category/${categorySlug}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Chip>{categoryAr}</Chip>
                  </Link>
                )}
                {store.location && <Chip>{store.location}</Chip>}
                {branchCount >= 2 && (
                  <Chip>{branchCountLabel(branchCount, toArabicNumerals)}</Chip>
                )}
              </div>

              {store.platforms && (
                <StoreSocialLinks platforms={store.platforms} store={store} />
              )}
            </div>

            {/* Rating */}
            <RatingBlock
              community={community}
              google={google}
              googleHref={googleHref}
              onGoogleClick={handleGoogleBadgeClick}
            />

            {/* CTA */}
            <CTAColumn onScrollToReviews={onScrollToReviews} />
          </div>
        </div>
      </div>

      {/* ── Mobile (< lg / 1024px) ── */}
      <div
        className="block lg:hidden"
        style={{
          background: "var(--ys-paper)",
          borderBottom: "1px solid var(--ys-rule)",
          direction: "rtl",
        }}
      >
        {/* Breadcrumb strip */}
        <div
          style={{
            padding: "8px 16px",
            fontFamily: "var(--ys-font)",
            fontSize: 11,
            color: "var(--ys-ink-3)",
            borderBottom: "1px solid var(--ys-rule)",
          }}
        >
          {breadcrumb}
        </div>

        {/* Body */}
        <div style={{ padding: "12px 16px 16px" }}>
          {/* Top row: logo + identity */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "64px 1fr",
              gap: 12,
              alignItems: "start",
            }}
          >
            <LogoBox
              store={store}
              size={64}
              isEditMode={isEditMode}
              logoRef={logoRef}
              onLogoUpload={onLogoUpload}
              isUploadingLogo={isUploadingLogo}
            />
            <div>
              {/* Verified pill */}
              <div
                style={{
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <VerifiedPill verified={Boolean(store.verified || store.hasVerifiedOwner)} />
                <StoreVerifiedBadge show={store.hasVerifiedOwner} />
                <CampaignBadge status={auditStatus} />
              </div>

              <div
                style={{
                  fontFamily: "var(--ys-font)",
                  fontWeight: 600,
                  fontSize: 21,
                  margin: 0,
                  lineHeight: 1.1,
                  color: "var(--ys-ink)",
                  display: "flex",
                  alignItems: "baseline",
                  flexWrap: "wrap",
                  gap: "0 6px",
                }}
              >
                {store.name}
                {store.nameEn && (
                  <span
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontWeight: 600,
                      fontSize: 15,
                      color: "var(--ys-primary)",
                      verticalAlign: "1px",
                    }}
                  >
                    {store.nameEn}
                  </span>
                )}
              </div>

              {/* Category + location chips */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  marginTop: 6,
                }}
              >
                {categoryAr && (
                  <Link
                    to={`/category/${categorySlug}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Chip>{categoryAr}</Chip>
                  </Link>
                )}
                {store.location && <Chip>{store.location}</Chip>}
                {branchCount >= 2 && (
                  <Chip>{branchCountLabel(branchCount, toArabicNumerals)}</Chip>
                )}
              </div>
            </div>
          </div>

          {/* Social links */}
          {store.platforms && (
            <StoreSocialLinks platforms={store.platforms} store={store} />
          )}

          {/* Rating block */}
          {primary ? (
            <div
              style={{
                marginTop: 10,
                padding: "10px 0",
                borderTop: "1px solid var(--ys-rule)",
                borderBottom: "1px solid var(--ys-rule)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontWeight: 700,
                      fontSize: 28,
                      lineHeight: 1,
                      color: "var(--ys-ink)",
                      direction: "ltr",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatRating(primary.score)}
                    <sub
                      style={{
                        fontFamily: "var(--ys-font)",
                        fontSize: 11,
                        color: "var(--ys-ink-3)",
                        fontWeight: 400,
                      }}
                    >
                      {" / ٥"}
                    </sub>
                  </div>
                  <div>
                    <div
                      style={{
                        direction: "ltr",
                        color: "var(--ys-star)",
                        fontSize: 14,
                      }}
                    >
                      {[1, 2, 3, 4, 5].map((i) => (
                        <span
                          key={i}
                          style={{ opacity: i <= Math.round(primary.score) ? 1 : 0.3 }}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--ys-font)",
                        fontSize: 12,
                        color: "var(--ys-ink-3)",
                        marginTop: 3,
                        direction: "rtl",
                      }}
                    >
                      {primaryIsCommunity
                        ? `مبني على ${toArabicNumerals(primary.count)} تقييم`
                        : googleSublineText(primary)}
                    </div>
                  </div>
                </div>
                <MobileReviewButton onScrollToReviews={onScrollToReviews} />
              </div>

              {primaryIsCommunity && google && (
                <div style={{ marginTop: 10, direction: "rtl" }}>
                  <GoogleBadge
                    source={google}
                    href={googleHref}
                    onClick={handleGoogleBadgeClick}
                  />
                </div>
              )}

              {!primaryIsCommunity && (
                <div
                  style={{
                    marginTop: 8,
                    fontFamily: "var(--ys-font)",
                    fontSize: 12,
                    color: "var(--ys-ink-3)",
                    direction: "rtl",
                  }}
                >
                  كن أول من يقيّم على يتفرّج
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                marginTop: 10,
                padding: "10px 0",
                borderTop: "1px solid var(--ys-rule)",
                borderBottom: "1px solid var(--ys-rule)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 13,
                  color: "var(--ys-ink-3)",
                }}
              >
                لم يقيّم بعد · كن أول مقيّم
              </span>
              <MobileReviewButton onScrollToReviews={onScrollToReviews} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
